import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noIndex?: boolean;
  siteName?: string;
  twitterHandle?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'CEO Keynotes - Premier Speaker Booking Agency',
  description = 'Book world-class keynote speakers for corporate events, conferences, and meetings. Transform your organization with inspiring speakers.',
  keywords = 'keynote speakers, corporate events, motivational speakers, business conferences',
  image = '/logo512.png',
  url = '',
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  noIndex = false,
  siteName = 'CEO Keynotes',
  twitterHandle = '@CEOKeynotes',
}) => {
  const fullTitle = title.includes('CEO Keynotes') ? title : `${title} | CEO Keynotes`;
  const fullUrl = url ? `${window.location.origin}${url}` : window.location.href;
  const fullImage = image.startsWith('http') ? image : `${window.location.origin}${image}`;

  // Helper to remove ravespeakers.com from text
  const cleanSourceDomain = (text: string) => {
    if (!text) return '';
    return text.replace(/https?:\/\/(?:www\.)?ravespeakers\.com/gi, '');
  };

  const cleanedDescription = cleanSourceDomain(description);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={cleanedDescription} />
      <meta name="keywords" content={keywords} />
      {author && <meta name="author" content={author} />}
      
      {/* Robots */}
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="googlebot" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={cleanedDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={cleanedDescription} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />

      {/* Article specific tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Favicon and Icons */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo192.png" />

      {/* Structured Data for Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "CEO Keynotes",
          "description": "Premier speaker booking agency connecting organizations with world-class keynote speakers",
          "url": window.location.origin,
          "logo": `${window.location.origin}/logo512.png`,
          "sameAs": [
            // TODO Add your social media URLs here
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "description": "Speaker booking inquiries"
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEO;